import { IntlShape } from 'react-intl';

import { TAuthError } from '@/redux/modules/auth';
import { TAccountType } from '@/services/types/auth/ILoginForm';
import { getNameErrorMessage } from '@/utility/getNameErrorMessage';
import { ENameRejection } from '@/utility/nameValidation';

/**
 * Backend (api-go) serror `code` values returned by the /v0/auth/creds/* endpoints.
 * These let us map a raw API error onto friendly, actionable copy instead of
 * showing the bare backend string (or, on signup today, nothing at all).
 */
enum EAuthErrorCode {
  EmailAlreadyTaken = 'UserEmailAlreadyTaken',
  BadLogin = 'UserBadLogin',
  SocialLoginDisabled = 'UserSocialLoginDisabled',
  // Returned when a password is entered for an account that only has social login.
  // Generic fallback used when the backend can't (or doesn't yet) name the provider.
  UsesSocialLogin = 'UserUsesSocialLogin',
  // Provider-specific variants — let us offer a "Continue with {provider}" prompt.
  // The frontend degrades to the generic message above if these aren't sent yet.
  UsesGoogleLogin = 'UserUsesGoogleLogin',
  UsesFacebookLogin = 'UserUsesFacebookLogin',
  UsesAppleLogin = 'UserUsesAppleLogin',
  TooManyAttempts = 'UserTooManyAttempts',
  // MP-6863: the Latin-script name policy. Two codes, one per cause. The copy for
  // both lives in `@/utility/getNameErrorMessage` so the inline client rules and
  // this server-error mapper cannot word the same rejection differently.
  NameNotLatinScript = 'NameNotLatinScript',
  NameInvalidCharacters = 'NameInvalidCharacters',
}

/**
 * True when a code is one of the two name rejections. Callers use this to route
 * the message onto the name field instead of the form-level error banner.
 */
export const isNameRejectionCode = (code?: string): boolean =>
  code === EAuthErrorCode.NameNotLatinScript || code === EAuthErrorCode.NameInvalidCharacters;

const SOCIAL_PROVIDER_BY_CODE: Partial<
  Record<EAuthErrorCode, { provider: TAccountType; label: string }>
> = {
  [EAuthErrorCode.UsesGoogleLogin]: { provider: 'google', label: 'Google' },
  [EAuthErrorCode.UsesFacebookLogin]: { provider: 'facebook', label: 'Facebook' },
  [EAuthErrorCode.UsesAppleLogin]: { provider: 'apple', label: 'Apple' },
};

interface IAuthErrorDisplay {
  message: string;
  /**
   * When true the caller should surface a "log in instead" affordance — i.e. the
   * user tried to create an account with an email that already exists (MP-5950 #4).
   */
  showLoginCta?: boolean;
  /**
   * When set, the account uses this social provider — the caller should offer a
   * "Continue with {provider}" prompt instead of a plain message.
   */
  socialProvider?: TAccountType;
}

/** Pull the backend `code` off a TAuthError, when present. */
export const getAuthErrorCode = (error?: TAuthError | null): string | undefined => {
  if (error && typeof error === 'object' && typeof error.code === 'string') {
    return error.code;
  }
  return undefined;
};

/**
 * Map a TAuthError onto a user-facing message. Returns null when there's no error.
 * Unknown codes fall back to the backend-provided message, then a generic message.
 */
export const getAuthErrorMessage = (
  intl: IntlShape,
  error?: TAuthError | null,
): IAuthErrorDisplay | null => {
  if (!error) return null;

  const code = getAuthErrorCode(error);
  const rawMessage = typeof error === 'string' ? error : error.error;

  // Backend message first, then a generic line. Shared by the default branch and
  // by any case that recognises a code but can't produce copy for it.
  const fallback = (): IAuthErrorDisplay => ({
    message:
      rawMessage ||
      intl.formatMessage({
        id: 'E7dg6I',
        defaultMessage: 'Oops, something went wrong. Try again later.',
        description: 'Auth error > Generic fallback when no specific message is available',
      }),
  });

  switch (code) {
    case EAuthErrorCode.EmailAlreadyTaken:
      return {
        message: intl.formatMessage({
          id: 'pAd0nP',
          defaultMessage: 'An account with this email already exists.',
          description: 'Auth error > Signup with an email that is already registered',
        }),
        showLoginCta: true,
      };
    case EAuthErrorCode.SocialLoginDisabled:
      return {
        message: intl.formatMessage({
          id: 'Wi8dYM',
          defaultMessage:
            'This email is registered with a password. Log in with your email and password instead.',
          description: 'Auth error > Social login attempted on a password-only account',
        }),
      };
    case EAuthErrorCode.UsesSocialLogin:
      return {
        message: intl.formatMessage({
          id: 'QncaHh',
          defaultMessage:
            'This account uses social login. Continue with the social account you used to sign up.',
          description:
            'Auth error > Password entered for a social-login (Google/Facebook/Apple) account',
        }),
      };
    case EAuthErrorCode.UsesGoogleLogin:
    case EAuthErrorCode.UsesFacebookLogin:
    case EAuthErrorCode.UsesAppleLogin: {
      const social = SOCIAL_PROVIDER_BY_CODE[code as EAuthErrorCode]!;
      return {
        message: intl.formatMessage(
          {
            id: 'eJ4q0+',
            defaultMessage: 'You’ve logged in with {provider} before.',
            description:
              'Auth error > Password entered for an account created with a specific social provider',
          },
          { provider: social.label },
        ),
        socialProvider: social.provider,
      };
    }
    case EAuthErrorCode.TooManyAttempts:
      return {
        message: intl.formatMessage({
          id: 'PR0KiL',
          defaultMessage: 'Too many attempts. Please wait a few minutes and try again.',
          description: 'Auth error > Rate limited after too many login/signup attempts',
        }),
      };
    // The code rides on the TOP-LEVEL `code`, never inside `errors[]` — serrors
    // serializes nested validation entries as {error, field} only. `errors[0].field`
    // says which of first_name/last_name failed; see getNameFieldError.
    case EAuthErrorCode.NameNotLatinScript:
    case EAuthErrorCode.NameInvalidCharacters: {
      const rejection =
        code === EAuthErrorCode.NameNotLatinScript
          ? ENameRejection.NotLatinScript
          : ENameRejection.InvalidCharacters;
      // getNameErrorMessage only returns null for ENameRejection.Ok, which neither
      // branch can produce — but fall through to the shared default rather than
      // assert, so a future third code can't turn this into an undefined message.
      const message = getNameErrorMessage(intl, rejection);
      return message ? { message } : fallback();
    }
    case EAuthErrorCode.BadLogin:
      return {
        message: intl.formatMessage({
          id: 'apBL5e',
          defaultMessage: 'The email or password you entered is incorrect.',
          description: 'Auth error > Incorrect email or password on login',
        }),
      };
    default:
      return fallback();
  }
};
